import { Globe, LayoutGrid, LogOut, type LucideIcon, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AdminMenu } from "./components/AdminMenu";
import { AppFooter } from "./components/AppFooter";
import { HubFeedbackButton } from "./components/HubFeedbackButton";
import { HubWaffleButton } from "./components/HubWaffleButton";
import { MindcodeTokenCard } from "./components/MindcodeTokenCard";
import { OnboardWizard } from "./components/OnboardWizard";
import { SignedOut } from "./components/SignedOut";
import { useApi } from "./hooks/useApi";
import { ApiError, api } from "./lib/api";
import { isLoggedOut, markLoggedOut } from "./lib/auth-redirect";
import { AdminAnalytics } from "./pages/AdminAnalytics";
import { AdminAppDetail } from "./pages/AdminAppDetail";
import { AdminApps } from "./pages/AdminApps";
import { AdminAudit } from "./pages/AdminAudit";
import { AdminGitHosts } from "./pages/AdminGitHosts";
import { AdminHealth } from "./pages/AdminHealth";
import { AdminSalesforce } from "./pages/AdminSalesforce";
import { AdminUsers } from "./pages/AdminUsers";
import { Launchpad } from "./pages/Launchpad";

type View =
  | "launchpad"
  | "onboard"
  | "admin-apps"
  | "admin-audit"
  | "admin-analytics"
  | "admin-users"
  | "admin-salesforce"
  | "admin-git-hosts"
  | "admin-health"
  | "admin-app-detail";

export function App(): JSX.Element {
  const { t, i18n } = useTranslation();
  const me = useApi(() => api.me(), []);
  const branding = useApi(() => api.branding(), []);
  const onboardingMode = useApi(() => api.onboardingMode(), []);
  const [view, setView] = useState<View>("launchpad");
  const [detailAppId, setDetailAppId] = useState<string | null>(null);
  // #6: optionaler Start-Tab der Detailseite (Teilen-Icon springt auf "share").
  const [detailInitialTab, setDetailInitialTab] = useState<"share" | undefined>(undefined);
  // View to return to when the onboarding page is cancelled.
  const [onboardReturnView, setOnboardReturnView] = useState<View>("launchpad");
  // View to return to from the app detail page (launchpad vs. admin list).
  const [detailReturnView, setDetailReturnView] = useState<View>("admin-apps");
  // Bumped after a successful onboarding to force the active list view to refetch.
  const [contentKey, setContentKey] = useState(0);

  const appTitle = branding.status === "ready" ? branding.data.app_title : "";
  // #8: Sharing-UI nur im Showroom-Modus zeigen (sonst strukturell wirkungslos).
  const sharingEnabled = branding.status === "ready" && branding.data.sharing_enabled;

  // "App hinzufügen" is offered to admins always, and to plain users when the
  // self-registration mode is not "off" (#34).
  const canOnboard =
    me.status === "ready" &&
    (me.data.is_admin || (onboardingMode.status === "ready" && onboardingMode.data.mode !== "off"));

  const openOnboard = (): void => {
    setOnboardReturnView(view);
    setView("onboard");
  };

  // Open an app's management/detail page, remembering where to return to.
  const openAppDetail = (appId: string, tab?: "share"): void => {
    setDetailAppId(appId);
    setDetailInitialTab(tab);
    setDetailReturnView(view);
    setView("admin-app-detail");
  };

  const onAppCreated = (): void => {
    const toAdminList = me.status === "ready" && (me.data.is_admin || me.data.is_operator);
    setView(toAdminList ? "admin-apps" : "launchpad");
    setContentKey((k) => k + 1);
  };

  const toggleLang = (): void => {
    const next = i18n.language.startsWith("de") ? "en" : "de";
    void i18n.changeLanguage(next);
  };

  const onLogout = (): void => {
    // Tab-Session als bewusst abgemeldet markieren, BEVOR navigiert wird (#95).
    // Verhindert nach dem Entra-Logout-Round-Trip die stille Re-Anmeldung.
    markLoggedOut();
    const form = document.createElement("form");
    form.method = "POST";
    form.action = "/auth/logout";
    document.body.appendChild(form);
    form.submit();
  };

  const showAdmin = me.status === "ready" && (me.data.is_admin || me.data.is_operator);

  useEffect(() => {
    if (appTitle) {
      document.title = appTitle;
    }
  }, [appTitle]);

  // Nach bewusstem Logout (#95): Hub-Cookie ist weg -> /api/me liefert 401. Statt
  // still auf /auth/login zu bouncen (was die lebende Entra-SSO-Session sofort
  // wieder einloggen wuerde), den abgemeldeten Zustand explizit anzeigen.
  const meUnauthorized =
    me.status === "error" && me.error instanceof ApiError && me.error.status === 401;
  if (meUnauthorized && isLoggedOut()) {
    return <SignedOut />;
  }

  return (
    <div className="app-shell">
      <header className="app-shell__header">
        <div className="app-shell__brand">
          <HubWaffleButton onClick={() => setView("launchpad")} />
          {me.status === "ready" ? <HubFeedbackButton /> : null}
          <h1 className="app-shell__title">{appTitle}</h1>
          {me.status === "ready" ? (
            <span className="app-shell__user">
              {me.data.display_name ?? me.data.email ?? me.data.user_id}
              {me.data.is_admin ? <em className="badge">{t("role.admin")}</em> : null}
              {me.data.is_operator ? <em className="badge">{t("role.operator")}</em> : null}
            </span>
          ) : null}
        </div>
        <nav className="app-shell__actions">
          <NavButton
            icon={LayoutGrid}
            active={view === "launchpad"}
            onClick={() => setView("launchpad")}
          >
            {t("nav.launchpad")}
          </NavButton>
          {canOnboard ? (
            <button type="button" className="nav-btn nav-btn--cta" onClick={openOnboard}>
              <Plus size={15} strokeWidth={2.5} aria-hidden="true" />
              {t("launchpad.addApp")}
            </button>
          ) : null}
          {showAdmin && me.status === "ready" ? (
            <AdminMenu isAdmin={me.data.is_admin} activeView={view} onSelect={(v) => setView(v)} />
          ) : null}
          <button type="button" onClick={toggleLang} className="lang-toggle">
            <Globe size={14} strokeWidth={2.5} aria-hidden="true" />
            {i18n.language.startsWith("de") ? "EN" : "DE"}
          </button>
          {me.status === "ready" ? (
            <button type="button" onClick={onLogout} className="lang-toggle">
              <LogOut size={14} strokeWidth={2.5} aria-hidden="true" />
              {t("auth.logout")}
            </button>
          ) : null}
        </nav>
      </header>
      <main className="app-shell__content">
        {me.status === "ready" && me.data.is_admin ? <MindcodeTokenCard /> : null}
        {view === "launchpad" ? (
          <Launchpad
            key={contentKey}
            canManageAll={showAdmin}
            onManageApp={openAppDetail}
            sharingEnabled={sharingEnabled}
          />
        ) : null}
        {view === "onboard" && canOnboard ? (
          <OnboardWizard
            endpoint="user"
            onCancel={() => setView(onboardReturnView)}
            onCreated={onAppCreated}
          />
        ) : null}
        {view === "admin-apps" && showAdmin ? (
          <AdminApps
            key={contentKey}
            onOpenDetail={openAppDetail}
            sharingEnabled={sharingEnabled}
          />
        ) : null}
        {/* App-Detail/Verwaltung: für Admin/Operator (alle Apps) und für Owner der
            jeweiligen App erreichbar. Das Backend erzwingt den Zugriff pro App
            ("Owner = Admin für eigene App"); hier nur das Routing. */}
        {view === "admin-app-detail" && detailAppId ? (
          <AdminAppDetail
            appId={detailAppId}
            {...(detailInitialTab ? { initialTab: detailInitialTab } : {})}
            sharingEnabled={sharingEnabled}
            onBack={() => {
              setView(detailReturnView);
              setDetailAppId(null);
            }}
          />
        ) : null}
        {view === "admin-users" && me.status === "ready" && me.data.is_admin ? (
          <AdminUsers />
        ) : null}
        {view === "admin-audit" && showAdmin ? <AdminAudit /> : null}
        {view === "admin-analytics" && me.status === "ready" && me.data.is_admin ? (
          <AdminAnalytics />
        ) : null}
        {view === "admin-health" && me.status === "ready" && me.data.is_admin ? (
          <AdminHealth />
        ) : null}
        {view === "admin-salesforce" && me.status === "ready" && me.data.is_admin ? (
          <AdminSalesforce />
        ) : null}
        {view === "admin-git-hosts" && me.status === "ready" && me.data.is_admin ? (
          <AdminGitHosts />
        ) : null}
        {me.status === "error" && me.error instanceof ApiError && me.error.status === 401 ? (
          <p className="launchpad__hint">{t("launchpad.redirecting")}</p>
        ) : null}
      </main>
      <AppFooter />
    </div>
  );
}

function NavButton(props: {
  active: boolean;
  onClick: () => void;
  icon: LucideIcon;
  children: React.ReactNode;
}): JSX.Element {
  const Icon = props.icon;
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={props.active ? "nav-btn nav-btn--active" : "nav-btn"}
    >
      <Icon size={15} strokeWidth={2.5} aria-hidden="true" />
      {props.children}
    </button>
  );
}
